// ---------------------------------------------------------------------------
// Lighting Zones — Per-area lighting with LightProbe placement
// and point/spot lights for indoor/tunnel areas.
// ---------------------------------------------------------------------------

import * as THREE from 'three';
import { LightProbeGenerator } from 'three/examples/jsm/lights/LightProbeGenerator.js';
import { markEffect } from '../renderer/effect-loop';

// ---------------------------------------------------------------------------
// Zone-based light probe system
// ---------------------------------------------------------------------------

export interface LightProbeZone {
  label: string;
  position: [number, number, number];
  /** Ambient color for this zone (warm/cool/neutral). */
  ambientColor: number;
  /** Ambient intensity. */
  intensity: number;
  /** Radius of influence for this probe. */
  radius: number;
}

const lightProbes: THREE.LightProbe[] = [];

/**
 * Place light probes at key map locations for localized ambient lighting.
 * Zero runtime cost after setup — baked into the environment.
 */
export function setupLightProbes(
  scene: THREE.Scene,
  renderer: THREE.WebGLRenderer,
  zones: LightProbeZone[],
): void {
  // Idempotent: strip any probes from a previous map load. Otherwise callers
  // that skip disposeLightingZones() between map changes accumulate probes.
  for (const probe of lightProbes) probe.removeFromParent();
  lightProbes.length = 0;

  const pmremGenerator = new THREE.PMREMGenerator(renderer);

  for (const zone of zones) {
    // Create a simple ambient light probe from a solid color
    // (In production, you'd use LightProbeGenerator.fromCubeRenderTarget)
    const probe = new THREE.LightProbe();

    // Set SH coefficients for a uniform color ambient
    const color = new THREE.Color(zone.ambientColor);
    const sh = probe.sh;

    // L0 band (constant ambient) — sqrt(1/4π) * color * intensity
    const c0 = Math.sqrt(1 / (4 * Math.PI)) * zone.intensity;
    sh.coefficients[0].set(color.r * c0, color.g * c0, color.b * c0);

    probe.position.set(...zone.position);
    scene.add(probe);
    lightProbes.push(probe);
  }

  pmremGenerator.dispose();
}

// ---------------------------------------------------------------------------
// Indoor area point/spot lights
// ---------------------------------------------------------------------------

export interface IndoorLight {
  label: string;
  type: 'point' | 'spot';
  position: [number, number, number];
  color: number;
  intensity: number;
  distance: number;
  decay?: number;
  // Spot-specific
  target?: [number, number, number];
  angle?: number;
  penumbra?: number;
  castShadow?: boolean;
}

const indoorLights: THREE.Light[] = [];
const fixtureMeshes: THREE.Mesh[] = [];
/** Explicitly track fixture materials for disposal (created per fixture). */
const fixtureMaterials: THREE.MeshStandardMaterial[] = [];

/**
 * Create a small emissive mesh at a light position so the source is visible.
 * High emissiveIntensity + toneMapped:false drives HDR bloom.
 */
function createLightFixture(
  scene: THREE.Scene,
  position: [number, number, number],
  color: number,
  type: 'point' | 'spot',
): void {
  let geometry: THREE.BufferGeometry;
  if (type === 'spot') {
    // Spot lights get a downward-facing cone/cylinder fixture
    geometry = new THREE.CylinderGeometry(0.08, 0.15, 0.12, 8);
  } else {
    // Point lights get a small sphere (bulb)
    geometry = new THREE.SphereGeometry(0.1, 8, 6);
  }

  const material = new THREE.MeshStandardMaterial({
    color: 0x000000,           // base color black (emissive dominates)
    emissive: color,
    emissiveIntensity: 5.0,    // >1.0 drives HDR bloom
    toneMapped: false,          // CRITICAL: prevents tone mapping from clamping
  });
  fixtureMaterials.push(material);

  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(...position);

  // Don't cast shadows from tiny fixtures
  mesh.castShadow = false;
  mesh.receiveShadow = false;

  // Exclude from bullet raycasts — fixtures are decorative only
  markEffect(mesh);

  scene.add(mesh);
  fixtureMeshes.push(mesh);
}

/**
 * Add warm point lights and shadow-casting spots for indoor/tunnel areas.
 */
export function setupIndoorLights(
  scene: THREE.Scene,
  lights: IndoorLight[],
): void {
  // Idempotent: strip any prior indoor lights/fixtures so repeated calls
  // don't accumulate across map loads.
  for (const light of indoorLights) {
    light.removeFromParent();
    if ('dispose' in light) (light as THREE.PointLight).dispose();
  }
  indoorLights.length = 0;
  for (const mesh of fixtureMeshes) {
    mesh.removeFromParent();
    mesh.geometry.dispose();
    (mesh.material as THREE.MeshStandardMaterial).dispose();
  }
  fixtureMeshes.length = 0;
  for (const mat of fixtureMaterials) mat.dispose();
  fixtureMaterials.length = 0;

  for (const def of lights) {
    if (def.type === 'point') {
      const light = new THREE.PointLight(
        def.color, def.intensity, def.distance, def.decay ?? 2,
      );
      light.position.set(...def.position);
      if (def.castShadow) {
        light.castShadow = true;
        light.shadow.mapSize.set(512, 512);
      }
      scene.add(light);
      indoorLights.push(light);
      createLightFixture(scene, def.position, def.color, def.type);
    } else {
      const light = new THREE.SpotLight(
        def.color, def.intensity, def.distance,
        def.angle ?? Math.PI / 4, def.penumbra ?? 0.5, def.decay ?? 2,
      );
      light.position.set(...def.position);
      if (def.target) {
        light.target.position.set(...def.target);
        scene.add(light.target);
      }
      if (def.castShadow) {
        light.castShadow = true;
        light.shadow.mapSize.set(512, 512);
      }
      scene.add(light);
      indoorLights.push(light);
      createLightFixture(scene, def.position, def.color, def.type);
    }
  }
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

export function disposeLightingZones(): void {
  for (const probe of lightProbes) {
    probe.removeFromParent();
  }
  lightProbes.length = 0;

  for (const light of indoorLights) {
    light.removeFromParent();
    if ('dispose' in light) (light as THREE.PointLight).dispose();
  }
  indoorLights.length = 0;

  // Mesh materials are also tracked in `fixtureMaterials`; collect them into
  // a Set first so each material is disposed exactly once even if it appears
  // in both the mesh list and the tracked-materials list.
  const materialsToDispose = new Set<THREE.Material>();
  for (const mat of fixtureMaterials) materialsToDispose.add(mat);

  for (const mesh of fixtureMeshes) {
    mesh.removeFromParent();
    mesh.geometry.dispose();
    materialsToDispose.add(mesh.material as THREE.MeshStandardMaterial);
  }
  fixtureMeshes.length = 0;

  for (const mat of materialsToDispose) {
    mat.dispose();
  }
  fixtureMaterials.length = 0;
}
